import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";

import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import {
  fetchModules,
  ModulesxportsCSV,
  updateModuelsStatus,
} from "slices/modules/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const ModuleList = () => {
  document.title = "Module List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "module",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const selectModule = createSelector(
    (state: any) => state.modules,
    (mod) => ({
      modules: mod.modulesList,
      rowCount: mod.rowCount,
      loading: mod.loading,
      error: mod.error,
    })
  );

  const { modules, rowCount, loading, error } = useSelector(selectModule);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.module
  );

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Convert filterValue (filterField/filterValue) to ColumnFiltersState (id/value) format
  const convertedFilters = useMemo(() => {
    if (!colValue || !Array.isArray(colValue)) return [];
    return colValue.map((f: any) => ({
      id: f.filterField || f.id,
      value: f.filterValue !== undefined ? f.filterValue : f.value,
    }));
  }, [colValue]);

  useEffect(() => {
    dispatch(
      fetchModules({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: convertedFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    convertedFilters,
  ]);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Handle status toggle
  const handleStatusToggle = useCallback(
    (module: any) => {
      const updatedStatus = !module?.is_active;
      const updatedModule = {
        ...module,
        is_active: updatedStatus,
      };

      dispatch(updateModuelsStatus(updatedModule))
        .unwrap()
        .then(() => {
          toast.success(
            `Module ${updatedStatus ? "activated" : "deactivated"} successfully`
          );
          dispatch(
            fetchModules({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: convertedFilters,
            })
          );
        })
        .catch((err: any) => {
          toast.error("Failed to update status");
          console.error(err);
        });
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      convertedFilters,
    ]
  );

  // const handleExport = async () => {
  //   // Show loading modal
  //   showCustomSwal({
  //     title: "Exporting...",
  //     text: "Please wait while we export your data.",
  //     allowOutsideClick: false,
  //     allowEscapeKey: false,
  //     didOpen: () => {
  //       Swal.showLoading();
  //     },
  //   });

  //   try {
  //     const { blob, filename } = await ModulesxportsCSV({
  //       search,
  //       filters: convertedFilters,
  //     });

  //     const url = window.URL.createObjectURL(new Blob([blob]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", filename);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode?.removeChild(link);
  //     window.URL.revokeObjectURL(url);

  //     // Replace loading modal with success modal
  //     showCustomSwal({
  //       icon: "success",
  //       title: "Export Completed",
  //       text: "Export completed successfully.",
  //     });
  //   } catch (error: any) {
  //     console.error("Export failed", error);
  //     const errorMessage =
  //       error?.response?.data?.message ||
  //       error?.response?.data?.error ||
  //       error?.message ||
  //       "Failed to export CSV";

  //     // Replace loading modal with error modal
  //     showCustomSwal({
  //       icon: "error",
  //       title: "Export Failed",
  //       text: errorMessage,
  //     });
  //   }
  // };

  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <EditButton
              onClick={() => {
                navigate(`/edit-module-form`, {
                  state: { ...row?.original },
                });
              }}
            />
          ),
        },
      ]
    : [];
  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: "App Name",
        accessorKey: "app_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/modulemaster/dropdown/app_name",
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const mod = row.original;
          return (
            <StatusToggle
              isActive={mod.is_active}
              onChange={() => {
                handleStatusToggle(mod);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [handleStatusToggle, canUpdate, DynamicAction, pageIndex, pageSize]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Modules</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canCreate && (
                      <ActionButton
                        label="Add Module"
                        icon="bi bi-plus-circle"
                        navigateLink="/module-form"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={modules}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  //filterOptions={filterOptions}
                  SearchPlaceholder="Search modules..."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default ModuleList;
